/**
 * Auth Store
 * Alpine.js reactive store for authentication state
 */

import Alpine from 'alpinejs'
import type { AuthStore, FirebaseUser } from '../types'
import { subscribeToAuthState, signInWithGoogle, signOut, type AuthUser } from '../services/auth'

// ============================================================================
// Store Definition
// ============================================================================

export function createAuthStore(): AuthStore {
  return {
    user: null,
    isAdmin: false,
    loading: true,
    error: null,

    /**
     * Initialize auth state listener
     */
    init(): void {
      subscribeToAuthState((authUser: AuthUser | null) => {
        if (authUser) {
          this.user = {
            uid: authUser.uid,
            email: authUser.email,
            displayName: authUser.displayName,
            photoURL: authUser.photoURL,
          } as FirebaseUser
          this.isAdmin = authUser.isAdmin
        } else {
          this.user = null
          this.isAdmin = false
        }
        this.loading = false
        this.error = null
      })
    },

    /**
     * Sign in with Google
     */
    async signIn(): Promise<void> {
      this.loading = true
      this.error = null

      try {
        const authUser = await signInWithGoogle()
        this.user = {
          uid: authUser.uid,
          email: authUser.email,
          displayName: authUser.displayName,
          photoURL: authUser.photoURL,
        } as FirebaseUser
        this.isAdmin = authUser.isAdmin
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Sign in failed'
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Sign out
     */
    async signOut(): Promise<void> {
      this.loading = true
      this.error = null

      try {
        await signOut()
        this.user = null
        this.isAdmin = false
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Sign out failed'
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Check if user is authenticated
     */
    get isAuthenticated(): boolean {
      return this.user !== null
    },

    /**
     * Get user display name or email
     */
    get displayName(): string {
      if (!this.user) {
        return 'Guest'
      }
      return this.user.displayName ?? this.user.email ?? 'User'
    },

    /**
     * Get user initials for avatar
     */
    get initials(): string {
      if (!this.user?.displayName) {
        return '?'
      }
      return this.user.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },
  }
}

// ============================================================================
// Register Store
// ============================================================================

export function registerAuthStore(): void {
  Alpine.store('auth', createAuthStore())
}

// ============================================================================
// Type Declaration for Alpine
// ============================================================================

declare module 'alpinejs' {
  interface Stores {
    auth: AuthStore
  }
}
